import { DialogComponent } from "@theme/dialog";
import { onDocumentReady, setLocalStorage, getLocalStorage, getLenis } from "@theme/utilities";

/**
 * A link that should not interrupt.
 *
 * Add ?np=1 to any URL on the site and the subscribe offer stays shut --
 * for a first-time visitor as much as a returning one, since without it a
 * visitor with nothing in their browser is exactly who the popup fires at.
 * Meant for links handed out where the offer would be in the way: a campaign
 * that already makes it, a page being walked through with someone, a
 * screenshot.
 *
 * Remembered for the visit in sessionStorage rather than by setting the
 * popup's own key. Two reasons: the shopper carries it as they click through
 * to pages that do not have the parameter on them, and it does not spend their
 * "already seen it" window -- a later visit on an ordinary link behaves
 * normally, which it would not if this wrote the seven-day flag.
 */
const QUIET_PARAM = "np";
const QUIET_KEY = "crossways:popup-quiet";

function popupSilenced() {
  let asked = null;
  try {
    asked = new URLSearchParams(window.location.search).get(QUIET_PARAM);
  } catch {
    return false;
  }
  // ?np=0 is an explicit "no, do show it", so a link can undo the flag.
  const wants = asked !== null && asked !== "0" && asked !== "false";

  try {
    if (asked === "0" || asked === "false") {
      sessionStorage.removeItem(QUIET_KEY);
      return false;
    }
    if (wants) sessionStorage.setItem(QUIET_KEY, "1");
    return wants || sessionStorage.getItem(QUIET_KEY) === "1";
  } catch {
    // Private browsing can refuse storage. The parameter still works on the
    // page carrying it; it just will not follow them to the next one.
    return wants;
  }
}

class SubscribePopupComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    this.triggerOpen = this.dataset.triggerOpen;
    this.repeatOpen = this.dataset.repeatOpen;
    this.cookieName = "pebbletheme:popup";

    onDocumentReady(this.handleOpen.bind(this));
  }

  handleOpen() {
    if (Shopify.designMode) return;
    if (popupSilenced()) return;

    let savedData = getLocalStorage(this.cookieName);
    if (savedData && savedData.opened) {
      return false;
    }

    switch (this.triggerOpen) {
      case "delay":
        setTimeout(() => {
          this.show();
        }, 5000);
        break;
      case "scroll_down":
        this.triggerAfterScroll(700, this.show.bind(this));
        break;
      default:
        this.show();
        break;
    }
  }

  show() {
    this.setRepeatOpen();
    this.showDialog();
  }

  triggerAfterScroll(offset = 500, trigger) {
    const lenis = getLenis();
    let scrollHandler = null;

    if (lenis) {
      // Use Lenis scroll events (shared instance with other sections)
      scrollHandler = () => {
        const scrollTop = lenis.scroll;
        if (scrollTop > offset) {
          trigger();
          lenis.off("scroll", scrollHandler);
        }
      };
      lenis.on("scroll", scrollHandler);
    } else {
      // Fallback to native scroll if Lenis not available
      scrollHandler = () => {
        const scrollTop =
          window.pageYOffset ||
          (document.documentElement || document.body.parentNode || document.body).scrollTop ||
          0;
        if (scrollTop > offset) {
          trigger();
          window.removeEventListener("scroll", scrollHandler, { capture: false });
        }
      };
      window.addEventListener("scroll", scrollHandler, { passive: true });
    }
  }

  setRepeatOpen() {
    if (Shopify.designMode) return;

    const expires = this.#getRepeatOpenExpiryDays();
    setLocalStorage(this.cookieName, { opened: true }, expires);
  }

  #getRepeatOpenExpiryDays() {
    const EXPIRY_BY_KEY = {
      no_repeat: 365,
      every_30_mins: 1 / 2 / 24,
      every_1_hr: 1 / 24,
      every_6_hrs: 6 / 24,
      every_12_hrs: 1 / 2,
      every_day: 1,
      every_3_days: 3,
      every_week: 7,
      every_2_weeks: 14,
      every_month: 30,
    };

    return EXPIRY_BY_KEY[this.repeatOpen] ?? 7;
  }
}

if (!customElements.get("subscribe-popup-component")) {
  customElements.define("subscribe-popup-component", SubscribePopupComponent);
}
